import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  getMe,
  getMyGameHistory,
  getMyStats,
  getMyTransactions,
  getNotifyPrefs,
  getReferral,
  getRewardsStatus,
  postCheckin,
  postRelief,
  putNotifyPrefs,
  ApiError,
  type GameHistoryItem,
  type MeResponse,
  type MeStats,
  type NotifyPrefs,
  type ReferralInfo,
  type RewardsStatus,
  type Transaction,
} from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { LanguageSwitch } from '../components/LanguageSwitch'
import { useCountUp } from '../useCountUp'
import { EASE_OUT } from '../../../shared/motion.ts'
import { LoadNotice } from '../components/LoadNotice'
import { ReferralActions } from '../components/ReferralActions'
import { gameLabel } from '../gameLabels'

export function Profile() {
  const { t } = useTranslation()
  const { user, token, refreshBalance } = useAuth()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [txs, setTxs] = useState<Transaction[]>([])
  const [txsLoaded, setTxsLoaded] = useState(false)
  const [stats, setStats] = useState<MeStats | null>(null)
  const [historyGame, setHistoryGame] = useState('')
  const [history, setHistory] = useState<GameHistoryItem[]>([])
  const [historyTotal, setHistoryTotal] = useState(0)
  const historyFor = useRef('')
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState('')
  const [historyRetry, setHistoryRetry] = useState(0)
  const [rewards, setRewards] = useState<RewardsStatus | null>(null)
  const [notifyPrefs, setNotifyPrefs] = useState<NotifyPrefs | null>(null)
  const [notifyBusy, setNotifyBusy] = useState<keyof NotifyPrefs | null>(null)
  const [notifyErr, setNotifyErr] = useState('')
  const [referral, setReferral] = useState<ReferralInfo | null>(null)
  const [busy, setBusy] = useState<'checkin' | 'relief' | null>(null)
  const [rewardErr, setRewardErr] = useState('')
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileLoadError, setProfileLoadError] = useState('')
  const mounted = useRef(true)
  const profileLoadVersion = useRef(0)
  const claimInFlight = useRef(false)
  const notifyInFlight = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    void refreshBalance()
  }, [refreshBalance])

  const loadProfile = useCallback(async () => {
    if (!token) return
    const version = ++profileLoadVersion.current
    setProfileLoading(true)
    setProfileLoadError('')
    const results = await Promise.allSettled([
      getMe(token),
      getMyTransactions(token, 15),
      getRewardsStatus(token),
      getReferral(token),
      getMyStats(token),
      getNotifyPrefs(token),
    ])
    if (!mounted.current || version !== profileLoadVersion.current) return

    const [meResult, txResult, rewardsResult, referralResult, statsResult, notifyResult] = results
    if (meResult.status === 'fulfilled') setMe(meResult.value)
    if (txResult.status === 'fulfilled') {
      setTxs(txResult.value.transactions ?? [])
      setTxsLoaded(true)
    }
    if (rewardsResult.status === 'fulfilled') setRewards(rewardsResult.value)
    if (referralResult.status === 'fulfilled') setReferral(referralResult.value)
    if (statsResult.status === 'fulfilled') setStats(statsResult.value)
    if (notifyResult.status === 'fulfilled') setNotifyPrefs(notifyResult.value)

    if (results.some((result) => result.status === 'rejected')) {
      setProfileLoadError(t('profile.loadError'))
    }
    setProfileLoading(false)
  }, [t, token])

  useEffect(() => {
    void loadProfile()
  }, [loadProfile])

  useEffect(() => {
    if (!token || !historyGame) {
      setHistory([])
      setHistoryTotal(0)
      historyFor.current = ''
      setHistoryError('')
      setHistoryLoading(false)
      return
    }
    let cancelled = false
    setHistoryLoading(true)
    setHistoryError('')
    void (async () => {
      try {
        const body = await getMyGameHistory(token, historyGame, 8, 0)
        if (!cancelled) {
          setHistory(body.items ?? [])
          setHistoryTotal(body.total ?? 0)
          historyFor.current = historyGame
        }
      } catch {
        if (!cancelled) {
          if (historyFor.current !== historyGame) {
            setHistory([])
            setHistoryTotal(0)
          }
          setHistoryError(t('profile.historyLoadError'))
        }
      } finally {
        if (!cancelled) setHistoryLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [historyGame, historyRetry, t, token])

  const reloadAfterGrant = async () => {
    if (!token) return
    const [txResult, rewardsResult] = await Promise.allSettled([
      getMyTransactions(token, 15),
      getRewardsStatus(token),
    ])
    if (txResult.status === 'fulfilled') {
      setTxs(txResult.value.transactions ?? [])
      setTxsLoaded(true)
    }
    if (rewardsResult.status === 'fulfilled') setRewards(rewardsResult.value)
    if (txResult.status === 'rejected' || rewardsResult.status === 'rejected') {
      setProfileLoadError(t('profile.loadError'))
    }
    await refreshBalance()
  }

  const toggleNotify = async (key: keyof NotifyPrefs) => {
    if (!token || notifyBusy || !notifyPrefs || notifyInFlight.current) return
    notifyInFlight.current = true
    setNotifyBusy(key)
    setNotifyErr('')
    try {
      const next = await putNotifyPrefs(token, { [key]: !notifyPrefs[key] })
      setNotifyPrefs(next)
    } catch {
      setNotifyErr(t('profile.notifyUpdateError'))
    } finally {
      notifyInFlight.current = false
      setNotifyBusy(null)
    }
  }

  const claim = async (kind: 'checkin' | 'relief') => {
    if (!token || busy || claimInFlight.current) return
    claimInFlight.current = true
    setBusy(kind)
    setRewardErr('')
    try {
      if (kind === 'checkin') await postCheckin(token)
      else await postRelief(token)
      await reloadAfterGrant()
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'request_failed'
      setRewardErr(t(`errors.${code}`, { defaultValue: t('errors.request_failed') }))
    } finally {
      claimInFlight.current = false
      setBusy(null)
    }
  }

  const profile = me?.user ?? user
  const displayBalance = useCountUp(user?.balance ?? profile?.balance ?? 0)

  if (!user || !profile) return null

  const display =
    profile.display_name ||
    profile.username ||
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
    '—'
  const status = profile.status || 'active'
  const statusLabel =
    status === 'active' ? t('profile.statusActive') : status
  const hasTelegram =
    (me?.identities ?? []).some((i) => i.provider === 'telegram') ||
    !!profile.telegram_id
  const initial = (display.trim().charAt(0) || '?').toUpperCase()

  const txLabel = (tx: Transaction) => {
    const map: Record<string, string> = {
      bet: t('profile.txBet'),
      win: t('profile.txWin'),
      refund: t('profile.txRefund'),
      bonus: t('profile.txBonus'),
    }
    return map[tx.type] || tx.type
  }

  const gameKeys = Object.keys(stats?.by_game ?? {}).sort()

  return (
    <div className="page" aria-busy={profileLoading}>
      <h2 className="section-title">{t('profile.title')}</h2>

      {profileLoadError ? (
        <LoadNotice
          message={profileLoadError}
          onRetry={() => void loadProfile()}
          busy={profileLoading}
        />
      ) : null}

      <nav className="profile-section-nav" aria-label={t('profile.sectionNav')}>
        <a href="#profile-overview">{t('profile.sectionOverview')}</a>
        <a href="#profile-stats">{t('profile.statsTitle')}</a>
        <a href="#profile-invite">{t('profile.inviteTitle')}</a>
        <a href="#profile-rewards">{t('profile.rewardsTitle')}</a>
        <a href="#profile-notify">{t('profile.notifyTitle')}</a>
        <a href="#profile-transactions">{t('profile.transactions')}</a>
      </nav>

      <section className="profile-hero card profile-anchor" id="profile-overview">
        <div className="profile-hero-row">
          <span className="avatar avatar--lg" aria-hidden>
            {initial}
          </span>
          <div className="profile-hero-text">
            <p className="profile-name">{display}</p>
            <p className="profile-status">
              <span className={`status-dot${status === 'active' ? ' ok' : ''}`} />
              {statusLabel}
            </p>
          </div>
        </div>
        <div className="profile-balance-block">
          <span className="stat-label">{t('profile.balance')}</span>
          <span className="profile-balance-value">
            {Math.round(displayBalance)}
          </span>
        </div>
      </section>

      <h3 className="section-title profile-anchor" id="profile-settings">
        {t('profile.settingsTitle')}
      </h3>
      <section className="card stack settings-card">
        <div className="reward-row">
          <div className="reward-copy">
            <span className="stat-label">{t('profile.language')}</span>
            <span className="stat-value">{t('profile.languageHint')}</span>
          </div>
          <LanguageSwitch />
        </div>
      </section>

      <div className="card stack">
        <div className="stat">
          <span className="stat-label">{t('profile.memberId')}</span>
          <span className="stat-value mono">{profile.id}</span>
        </div>
        <div className="stat">
          <span className="stat-label">{t('profile.telegramId')}</span>
          <span className="stat-value mono">
            {hasTelegram
              ? profile.telegram_id || t('profile.boundTelegram')
              : '—'}
          </span>
        </div>
      </div>

      <h3 className="section-title profile-anchor" id="profile-stats">
        {t('profile.statsTitle')}
      </h3>
      <section className="card stack">
        <div className="stats-grid">
          {(
            [
              ['netProfit', stats?.net_profit ?? null],
              ['totalWagered', stats?.total_wagered ?? null],
              ['totalWon', stats?.total_won ?? null],
              ['gamesPlayed', stats?.games_played ?? null],
              ['biggestWin', stats?.biggest_win ?? null],
            ] as const
          ).map(([key, val]) => (
            <div className="stat" key={key}>
              <span className="stat-label">{t(`profile.${key}`)}</span>
              <span
                className={`stat-value mono${
                  key === 'netProfit' && val !== null
                    ? val < 0
                      ? ' bad'
                      : val > 0
                        ? ' good'
                        : ''
                    : ''
                }`}
              >
                {val === null ? '—' : key === 'netProfit' && val > 0 ? `+${val}` : val}
              </span>
            </div>
          ))}
        </div>
        {gameKeys.length > 0 ? (
          <>
            <p className="hint rewards-hint">{t('profile.byGame')}</p>
            <div className="game-chip-row">
              {gameKeys.map((g) => {
                const slice = stats?.by_game?.[g]
                return (
                  <button
                    key={g}
                    type="button"
                    className={`game-chip${historyGame === g ? ' is-on' : ''}`}
                    aria-pressed={historyGame === g}
                    onClick={() => setHistoryGame((cur) => (cur === g ? '' : g))}
                  >
                    {gameLabel(t, g)}
                    {slice ? ` · ${slice.games_played}` : ''}
                  </button>
                )
              })}
            </div>
          </>
        ) : null}
        {historyGame ? (
          <div className="stack">
            <p className="hint rewards-hint">
              {t('profile.gameHistory')} · {gameLabel(t, historyGame)}
              {historyTotal ? ` (${historyTotal})` : ''}
            </p>
            {historyError ? (
              <LoadNotice
                message={historyError}
                onRetry={() => setHistoryRetry((value) => value + 1)}
                busy={historyLoading}
              />
            ) : null}
            {historyFor.current !== historyGame ? (
              historyError ? null : <p className="hint" role="status">{t('app.loading')}</p>
            ) : history.length === 0 ? (
              <p className="hint">{t('profile.noHistory')}</p>
            ) : (
              history.map((it) => (
                <div className="stat tx-row" key={it.round_id}>
                  <span className="stat-label">
                    {it.wagered} → {it.won}
                    {it.refunded ? ` · ${t('profile.txRefund')} ${it.refunded}` : ''}
                  </span>
                  <span
                    className={`stat-value mono${it.net < 0 ? ' bad' : it.net > 0 ? ' good' : ''}`}
                  >
                    {t('profile.historyNet')} {it.net > 0 ? `+${it.net}` : it.net}
                  </span>
                </div>
              ))
            )}
          </div>
        ) : null}
      </section>

      <h3 className="section-title profile-anchor" id="profile-invite">
        {t('profile.inviteTitle')}
      </h3>
      <section className="card stack">
        <p className="hint rewards-hint">{t('profile.inviteHint')}</p>
        <div className="stat">
          <span className="stat-label">{t('profile.inviteCode')}</span>
          <span className="stat-value mono">
            {referral?.start_payload || referral?.code || '—'}
          </span>
        </div>
        <ReferralActions code={referral?.start_payload || referral?.code || ''} />
        <div className="stat">
          <span className="stat-label">{t('profile.invitees')}</span>
          <span className="stat-value mono">{referral ? referral.invitees_count : '—'}</span>
        </div>
        <div className="stat">
          <span className="stat-label">{t('profile.inviteRewards')}</span>
          <span className="stat-value mono">
            {referral ? `+${referral.rewards_total}` : '—'}
          </span>
        </div>
      </section>

      <h3 className="section-title profile-anchor" id="profile-rewards">
        {t('profile.rewardsTitle')}
      </h3>
      <section className="card stack rewards-card">
        <p className="hint rewards-hint">{t('profile.rewardsHint')}</p>
        <div className="reward-row">
          <div className="reward-copy">
            <span className="stat-label">{t('profile.checkin')}</span>
            <span className="stat-value mono">
              {rewards ? `+${rewards.checkin_amount}` : '—'}
            </span>
          </div>
          <button
            type="button"
            className="btn"
            disabled={!rewards?.checkin_available || busy !== null}
            onClick={() => void claim('checkin')}
          >
            {!rewards
              ? t('app.dataUnavailable')
              : rewards.checkin_available
                ? t('profile.claimCheckin')
                : t('profile.checkinDone')}
          </button>
        </div>
        <div className="reward-row">
          <div className="reward-copy">
            <span className="stat-label">{t('profile.relief')}</span>
            <span className="stat-value mono">
              {rewards ? `+${rewards.relief_amount}` : '—'}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!rewards?.relief_available || busy !== null}
            onClick={() => void claim('relief')}
          >
            {!rewards
              ? t('app.dataUnavailable')
              : rewards.relief_available
                ? t('profile.claimRelief')
                : t('profile.reliefUnavailable')}
          </button>
        </div>
        {rewards?.relief_cooldown_ends_at ? (
          <p className="hint">
            {t('profile.reliefCooldown', {
              time: new Date(rewards.relief_cooldown_ends_at).toLocaleString(),
            })}
          </p>
        ) : null}
        {rewardErr ? <p className="error" role="alert">{rewardErr}</p> : null}
      </section>

      <h3 className="section-title profile-anchor" id="profile-notify">
        {t('profile.notifyTitle')}
      </h3>
      <section className="card stack">
        <p className="hint rewards-hint">{t('profile.notifyHint')}</p>
        {(
          [
            ['checkin', 'notifyCheckin'],
            ['leaderboard', 'notifyLeaderboard'],
            ['bigwin', 'notifyBigwin'],
          ] as const
        ).map(([key, label]) => {
          const on = !!notifyPrefs?.[key]
          return (
            <div className="reward-row" key={key}>
              <div className="reward-copy">
                <span className="stat-label">{t(`profile.${label}`)}</span>
                <span className="stat-value">
                  {!notifyPrefs
                    ? t('app.dataUnavailable')
                    : on
                      ? t('profile.notifyOn')
                      : t('profile.notifyOff')}
                </span>
              </div>
              <button
                type="button"
                className={on ? 'btn btn-secondary' : 'btn'}
                aria-pressed={notifyPrefs ? on : undefined}
                disabled={!token || !notifyPrefs || notifyBusy !== null}
                onClick={() => void toggleNotify(key)}
              >
                {!notifyPrefs
                  ? t('app.dataUnavailable')
                  : on
                    ? t('profile.notifyOff')
                    : t('profile.notifyOn')}
              </button>
            </div>
          )
        })}
        {notifyErr ? <p className="error" role="alert">{notifyErr}</p> : null}
      </section>

      <h3 className="section-title">{t('profile.bindings')}</h3>
      <div className="card muted">
        {hasTelegram ? t('profile.boundTelegram') : '—'}
      </div>

      <h3 className="section-title profile-anchor" id="profile-transactions">
        {t('profile.transactions')}
      </h3>
      {txs.length === 0 ? (
        <div className="card muted">
          {txsLoaded ? t('profile.noTx') : t('app.dataUnavailable')}
        </div>
      ) : (
        <div className="card stack tx-list">
          {txs.map((tx, i) => (
            <motion.div
              key={tx.id}
              className="stat tx-row"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: Math.min(i * 0.03, 0.3),
                duration: 0.2,
                ease: EASE_OUT,
              }}
            >
              <span className="stat-label">
                {txLabel(tx)}
                {tx.game_type ? ` · ${gameLabel(t, tx.game_type)}` : ''}
              </span>
              <span
                className={`stat-value mono${tx.amount < 0 ? ' bad' : tx.amount > 0 ? ' good' : ''}`}
              >
                {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
              </span>
            </motion.div>
          ))}
        </div>
      )}
      <p className="hint">{t('profile.logoutHint')}</p>
    </div>
  )
}
